import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { UserDetail } from './UserDetail';
import { LoginHistoryManagement } from './LoginHistoryManagement';
import { AcquisitionSection } from './AcquisitionSection';
import { AttendanceManagement } from './AttendanceManagement';
import { ActivityScoreManagement } from './ActivityScoreManagement';
import { AccessDots } from './AccessDots';

export const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'활동점수' | '출석체크' | '전체' | '활성' | '비활성' | '로그인' | '획득'>(
    '활동점수',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRows, setVisibleRows] = useState(10);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const statusTabs: Array<'활동점수' | '출석체크' | '전체' | '활성' | '비활성' | '로그인' | '획득'> = [
    '활동점수',
    '출석체크',
    '전체',
    '활성',
    '비활성',
    '로그인',
    '획득',
  ];
  const rowsPerPage = 10;

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: async () => {
      const response = await adminApi.getUsers();
      return response.data || [];
    },
  });

  // 상태별 필터링
  const filteredByStatus =
    activeTab === '전체'
      ? users
      : activeTab === '활성'
        ? users.filter((user) => user.status === 'ACTIVE')
        : users.filter((user) => user.status === 'INACTIVE');

  // 검색 필터링
  const filteredData = searchQuery
    ? filteredByStatus.filter(
        (user) =>
          (user.userName?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
          (user.email?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()),
      )
    : filteredByStatus;

  // 페이지네이션
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // 모바일 "더 보기"
  const mobileData = filteredData.slice(0, visibleRows);

  const handleLoadMore = () => {
    setVisibleRows((prev) => Math.min(prev + 10, filteredData.length));
  };

  const getPlatformBadgeStyles = (platform: string) => {
    switch (platform) {
      case 'google':
        return 'bg-[#4285F4] text-white'; // Google Blue
      case 'kakao':
        return 'bg-[#FEE500] text-[#000000]'; // Kakao Yellow
      case 'TOSS':
        return 'bg-[#0064FF] text-white'; // Toss Blue
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPlatformDisplayName = (platform: string) => {
    return platform.toUpperCase();
  };

  if (selectedUserId !== null) {
    return <UserDetail userId={selectedUserId} onBack={() => setSelectedUserId(null)} />;
  }

  return (
    <div className="space-y-4">
      {/* Header + Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex flex-col gap-4">
          <h2 className="text-lg lg:text-xl font-bold text-[#030213]">회원 관리</h2>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#030213] focus:border-transparent"
            />
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2 mt-4 border-b border-[#E5E7EB]">
          {statusTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab ? 'text-[#030213]' : 'text-[#6B7280] hover:text-[#030213]'
              }`}
            >
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#030213]" />}
            </button>
          ))}
        </div>
      </div>

      {/* 활동점수/출석체크/로그인/획득 탭 */}
      {activeTab === '활동점수' ? (
        <ActivityScoreManagement onSelectUser={setSelectedUserId} />
      ) : activeTab === '출석체크' ? (
        <AttendanceManagement />
      ) : activeTab === '로그인' ? (
        <LoginHistoryManagement />
      ) : activeTab === '획득' ? (
        <AcquisitionSection />
      ) : (
        <>
          {isLoading ? (
            <div className="p-6 text-center text-[#6B7280]">로딩 중...</div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#f3f3f5]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        사용자
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        이메일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        가입 플랫폼
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        가입일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        최종 로그인
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                        상태
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E5E7EB]">
                    {paginatedData.map((user) => (
                      <tr
                        key={user.userId}
                        className="hover:bg-[#f3f3f5] transition-colors cursor-pointer"
                        onClick={() => setSelectedUserId(user.userId)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#f3f3f5] flex items-center justify-center">
                              {user.profileImageUrl ? (
                                <img
                                  src={user.profileImageUrl}
                                  alt={user.userName}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-[#6B7280] font-medium">
                                  {user.userName.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <div className="text-sm font-medium text-[#030213]">{user.userName}</div>
                              <AccessDots access={user.recentAccess} />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B7280]">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getPlatformBadgeStyles(user.socialPlatform)}`}
                          >
                            {getPlatformDisplayName(user.socialPlatform)}
                          </span>
                          {user.authType === 'ANONYMOUS' && (
                            <span
                              className={`ml-1 px-2 py-1 text-xs font-medium rounded-full ${
                                user.tossLinkedAt ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEF3C7] text-[#92400E]'
                              }`}
                            >
                              {user.tossLinkedAt ? '가입 완료' : '미가입'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                          {new Date(user.regDateTime).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              user.status === 'ACTIVE' ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEE2E2] text-[#991B1B]'
                            }`}
                          >
                            {user.status === 'ACTIVE' ? '활성' : '비활성'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      이전
                    </button>
                    <span className="text-sm text-[#6B7280]">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      다음
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {mobileData.map((user) => (
                  <div
                    key={user.userId}
                    className="bg-white rounded-lg shadow-sm p-4 cursor-pointer"
                    onClick={() => setSelectedUserId(user.userId)}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-[#f3f3f5] flex items-center justify-center">
                        {user.profileImageUrl ? (
                          <img
                            src={user.profileImageUrl}
                            alt={user.userName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-[#6B7280] font-medium text-lg">
                            {user.userName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[#030213]">{user.userName}</span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              user.status === 'ACTIVE' ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEE2E2] text-[#991B1B]'
                            }`}
                          >
                            {user.status === 'ACTIVE' ? '활성' : '비활성'}
                          </span>
                        </div>
                        <p className="text-xs text-[#6B7280] mb-1">{user.email}</p>
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPlatformBadgeStyles(user.socialPlatform)}`}
                          >
                            {getPlatformDisplayName(user.socialPlatform)}
                          </span>
                          {user.authType === 'ANONYMOUS' && (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                user.tossLinkedAt ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEF3C7] text-[#92400E]'
                              }`}
                            >
                              {user.tossLinkedAt ? '가입 완료' : '미가입'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#6B7280]">
                          가입일: {new Date(user.regDateTime).toLocaleDateString('ko-KR')}
                        </p>
                        <p className="text-xs text-[#6B7280]">
                          최종 로그인: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'}
                        </p>
                        <div className="mt-1 flex items-center gap-1">
                          <span className="text-xs text-[#6B7280]">최근 7일:</span>
                          <AccessDots access={user.recentAccess} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Load More Button */}
                {visibleRows < filteredData.length && (
                  <button
                    onClick={handleLoadMore}
                    className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
                  >
                    더 보기
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
